import type { ReactNode } from "react";
import { useState } from "react";

import { ChevronDown } from "lucide-react";

import Button from "../../Components/Button";
import {
  DH_SECTION_ACCORDION,
  DH_SECTION_ACCORDION_BODY,
  DH_SECTION_ACCORDION_COUNT,
  DH_SECTION_ACCORDION_HEADING,
  DH_SECTION_ACCORDION_HDR,
  DH_SECTION_ACCORDION_TITLE,
} from "../../Utils/Helper/discoveryHubUi";

type DiscoveryHubPollSectionAccordionProps = {
  title: string;
  count: number;
  pendingReminderCount?: number;
  onSendReminderToAll?: () => void;
  reminderLoading?: boolean;
  reminderDisabled?: boolean;
  headerAction?: ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  showCountBadge?: boolean;
  bodyClassName?: string;
  children: ReactNode;
};

const DiscoveryHubPollSectionAccordion = ({
  title,
  count,
  pendingReminderCount = 0,
  onSendReminderToAll,
  reminderLoading = false,
  reminderDisabled = false,
  headerAction,
  collapsible = false,
  defaultExpanded = true,
  showCountBadge = true,
  bodyClassName,
  children,
}: DiscoveryHubPollSectionAccordionProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const showReminderAction =
    !headerAction &&
    pendingReminderCount > 0 &&
    typeof onSendReminderToAll === "function";

  const toggleExpanded = () => {
    if (collapsible) setExpanded(prev => !prev);
  };

  return (
    <div className={DH_SECTION_ACCORDION}>
      <div className={DH_SECTION_ACCORDION_HDR}>
        <div
          className={`${DH_SECTION_ACCORDION_HEADING}${collapsible ? " cursor-pointer select-none" : ""}`}
          onClick={collapsible ? toggleExpanded : undefined}
          onKeyDown={
            collapsible
              ? e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleExpanded();
                  }
                }
              : undefined
          }
          role={collapsible ? "button" : undefined}
          tabIndex={collapsible ? 0 : undefined}
          aria-expanded={collapsible ? expanded : undefined}
        >
          {collapsible ? (
            <ChevronDown
              size={18}
              className={`shrink-0 text-gray-500 transition-transform ${expanded ? "" : "-rotate-90"}`}
              aria-hidden
            />
          ) : null}
          <span className={DH_SECTION_ACCORDION_TITLE}>{title}</span>
          {showCountBadge ? (
            <span className={DH_SECTION_ACCORDION_COUNT}>{count}</span>
          ) : null}
        </div>
        {headerAction ??
          (showReminderAction ? (
            <Button
              variant="outline-primary"
              loading={reminderLoading}
              disabled={reminderDisabled}
              onClick={onSendReminderToAll}
              className="shrink-0"
            >
              Send Reminder to All ({pendingReminderCount})
            </Button>
          ) : null)}
      </div>
      {!collapsible || expanded ? (
        <div
          className={
            bodyClassName
              ? `${DH_SECTION_ACCORDION_BODY} ${bodyClassName}`
              : DH_SECTION_ACCORDION_BODY
          }
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

export default DiscoveryHubPollSectionAccordion;
